import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import A2PBrandSummaryCard from '../../../../components/A2PBrandSummaryCard.jsx';
import A2PCampaignCard from '../../../../components/A2PCampaignCard.jsx';
import A2PCapacityBanner from '../../../../components/A2PCapacityBanner.jsx';
import CreateA2PCampaignWizard from '../../../settings/CreateA2PCampaignWizard.jsx';
import { fetchBrand, fetchCampaigns, fetchCapacity } from '../../../../api/a2p.js';
import {
  Plus,
} from 'lucide-react';

/**
 * Business Profile tab — A2P brand summary, campaigns and capacity.
 * @returns {JSX.Element}
 */
export default function BusinessProfile() {
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data: brandData, refetch: refetchBrand } = useQuery({
    queryKey: ['a2p-brand'],
    queryFn: () => fetchBrand().catch(() => ({ ok: false })),
  });
  const { data: campaignsData, refetch: refetchCampaigns } = useQuery({
    queryKey: ['a2p-campaigns'],
    queryFn: () => fetchCampaigns().catch(() => ({ ok: false, campaigns: [] })),
  });
  const { data: capacityData, refetch: refetchCapacity } = useQuery({
    queryKey: ['a2p-capacity'],
    queryFn: () => fetchCapacity().catch(() => ({ ok: false })),
  });

  function refetchAll() {
    refetchBrand();
    refetchCampaigns();
    refetchCapacity();
  }

  const brand = brandData?.brand || null;
  const campaigns = campaignsData?.campaigns || [];
  const capacity = capacityData?.capacity || { campaigns: [] };

  return (
    <div className="space-y-4">
      <A2PCapacityBanner capacity={capacity} onCreate={() => setWizardOpen(true)} />

      {brand ? (
        <A2PBrandSummaryCard brand={brand} />
      ) : (
        <div className="panel p-5">
          <div className="font-display font-bold text-lg mb-2">No business profile yet</div>
          <p className="text-sm text-white/60">Submit your brand via the existing A2P wizard before creating campaigns.</p>
        </div>
      )}

      <div className="panel p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">A2P Campaigns</div>
            <div className="text-xs text-white/55">
              Each campaign holds up to 49 numbers. Add a new one before you hit the cap.
            </div>
          </div>
          {brand && (
            <button onClick={() => setWizardOpen(true)} className="btn-primary inline-flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Create A2P Campaign
            </button>
          )}
        </div>

        {campaigns.length === 0 ? (
          <div className="text-sm text-white/55 py-6 text-center border border-dashed border-civix-border/50 rounded-md">
            You haven't created an A2P campaign yet. Click + Create A2P Campaign to start sending compliant SMS.
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <A2PCampaignCard key={c._id} campaign={c} onChange={refetchAll} />
            ))}
          </div>
        )}
      </div>

      <CreateA2PCampaignWizard
        open={wizardOpen}
        brand={brand}
        onClose={() => setWizardOpen(false)}
        onCreated={refetchAll}
      />
    </div>
  );
}
